// src/services/role.service.ts
import { ListApiResponse } from "@/types/common";
import { privateRequest } from "../axios";
import toast from "@/lib/toast";

export interface Role {
  _id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  deletedAt: string | null;
}

const ENDPOINTS = {
  ROLE: "/role",
  ROLE_EXPORT: "/role/export",
  ROLE_ID: (id: number | string) => `/role/${id}`,
  ROLE_PERMISSIONS: (id: number | string) => `/role/${id}/permission`,
};

const getRoles = async (params?: Record<string, any>) => {
  const response = await privateRequest.get<ListApiResponse<Role>>(
    ENDPOINTS.ROLE,
    { params }
  );
  return response.data.data; // 👈 unwrap correctly
};

// ✅ Get Role by ID
const getRoleById = async (id: number | string): Promise<Role> => {
  const response = await privateRequest.get<Role>(ENDPOINTS.ROLE_ID(id));
  return response.data;
};

// ✅ Add Role
const addRole = async (data: Omit<Role, "id">): Promise<Role> => {
  const response = await privateRequest.post<Role>(ENDPOINTS.ROLE, data);
  return response.data;
};

// ✅ Update Role
const updateRole = async (
  id: number | string,
  data: Partial<Role>
): Promise<Role> => {
  const response = await privateRequest.put<Role>(ENDPOINTS.ROLE_ID(id), data);
  return response.data;
};

// ✅ Delete Role
const deleteRole = async (
  id: number | string
): Promise<{ success: boolean; message?: string }> => {
  const response = await privateRequest.delete<{
    success: boolean;
    message?: string;
  }>(ENDPOINTS.ROLE_ID(id));
  return response.data;
};

const updateRolePermissions = async (id: string, body: any) => {
  const response = await privateRequest.post(
    ENDPOINTS.ROLE_PERMISSIONS(id),
    body
  );
  return response?.data;
};

const exportRoleData = async (params: any) => {
  const response = await privateRequest.post(
    ENDPOINTS.ROLE_EXPORT,
    null,
    {
      params,
    }
  );

  if (response?.data?.success) {
    toast.success(response?.data?.data);
  }
  return response?.data;
};

const RoleService = {
  getRoles,
  getRoleById,
  addRole,
  updateRole,
  deleteRole,
  updateRolePermissions,
  exportRoleData,
};

export default RoleService;
